import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { User } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

interface Profile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  user: 'Benutzer',
  super_admin: 'Superadministrator',
};

export default function ProfileTab() {
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery<Profile>({
    queryKey: ['account-profile'],
    queryFn: async () => (await api.get('/account/profile')).data,
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [initialized, setInitialized] = useState(false);

  if (profile && !initialized) {
    setFirstName(profile.first_name ?? '');
    setLastName(profile.last_name ?? '');
    setPhone(profile.phone ?? '');
    setInitialized(true);
  }

  const mutation = useMutation({
    mutationFn: (data: { first_name: string; last_name: string; phone: string }) =>
      api.put('/account/profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-profile'] });
      toast.success('Profil gespeichert');
    },
    onError: (err) => {
      toast.error((isAxiosError(err) ? err.response?.data?.detail : undefined) || 'Fehler beim Speichern');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ first_name: firstName, last_name: lastName, phone });
  };

  if (isLoading) {
    return (
      <Card variant="glass">
        <CardContent className="py-8 text-center text-muted-foreground">Wird geladen…</CardContent>
      </Card>
    );
  }

  return (
    <Card variant="glass">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <User className="h-5 w-5" />
          Profilinformationen
        </CardTitle>
        <CardDescription>Ihren Anzeigenamen und Kontaktdaten aktualisieren</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Read-only info */}
          <div className="grid gap-4 sm:grid-cols-2 pb-4 border-b border-border">
            <div>
              <p className="text-xs text-muted-foreground mb-1">E-Mail</p>
              <p className="text-sm font-medium">{profile?.email}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Rolle</p>
              <p className="text-sm font-medium">{ROLE_LABELS[profile?.role ?? ''] ?? profile?.role}</p>
            </div>
          </div>

          {/* Editable fields */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">Vorname</Label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="block w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                placeholder="Max"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Nachname</Label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="block w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                placeholder="Mustermann"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">Telefon</Label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="block w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              placeholder="+49 6772 123456"
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Wird gespeichert…' : 'Speichern'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
